import { supabase } from "./supabase";
import type { Accent, Board, BoardColumn, Card, Checklist, Comment, VideoRecord, WorkspaceProfile } from "../types/domain";

export interface WorkspaceSnapshot {
  boards: Board[];
  columns: BoardColumn[];
  cards: Card[];
  videos: VideoRecord[];
  profiles: WorkspaceProfile[];
}

type Row = Record<string, unknown>;

function client() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

function rows<T extends Row>(data: T[] | null, error: { message: string } | null): T[] {
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function loadWorkspaceSnapshot(): Promise<WorkspaceSnapshot> {
  const db = client();
  const [boardsResult, columnsResult, cardsResult, assigneesResult, checklistsResult, itemsResult, commentsResult, profilesResult, videosResult] = await Promise.all([
    db.from("boards").select("*").is("archived_at", null).order("position"),
    db.from("board_columns").select("*").is("archived_at", null).order("position"),
    db.from("cards").select("*").is("archived_at", null).order("position"),
    db.from("card_assignees").select("card_id,user_id"),
    db.from("checklists").select("*").order("position"),
    db.from("checklist_items").select("*").order("position"),
    db.from("comments").select("id,card_id,author_id,body,created_at").is("archived_at", null).order("created_at"),
    db.from("profiles").select("id,email,display_name"),
    db.from("videos").select("*").is("archived_at", null).order("created_at"),
  ]);

  const boardRows = rows(boardsResult.data as Row[] | null, boardsResult.error);
  const columnRows = rows(columnsResult.data as Row[] | null, columnsResult.error);
  const cardRows = rows(cardsResult.data as Row[] | null, cardsResult.error);
  const assigneeRows = rows(assigneesResult.data as Row[] | null, assigneesResult.error);
  const checklistRows = rows(checklistsResult.data as Row[] | null, checklistsResult.error);
  const itemRows = rows(itemsResult.data as Row[] | null, itemsResult.error);
  const commentRows = rows(commentsResult.data as Row[] | null, commentsResult.error);
  const profileRows = rows(profilesResult.data as Row[] | null, profilesResult.error);
  const videoRows = rows(videosResult.data as Row[] | null, videosResult.error);

  const profiles: WorkspaceProfile[] = profileRows.map((row) => ({
    id: String(row.id),
    email: String(row.email),
    displayName: String(row.display_name),
    role: "admin",
    initials: initials(String(row.display_name)),
  }));
  const profileNames = new Map(profiles.map((profile) => [profile.id, profile.displayName]));

  const checklistsByCard = new Map<string, Checklist[]>();
  for (const checklistRow of checklistRows) {
    const checklistId = String(checklistRow.id);
    const cardId = String(checklistRow.card_id);
    const checklist: Checklist = {
      id: checklistId,
      title: String(checklistRow.title),
      position: Number(checklistRow.position),
      items: itemRows.filter((item) => String(item.checklist_id) === checklistId).map((item) => ({
        id: String(item.id),
        title: String(item.title),
        completed: Boolean(item.completed),
        position: Number(item.position),
      })),
    };
    checklistsByCard.set(cardId, [...(checklistsByCard.get(cardId) ?? []), checklist]);
  }

  const commentsByCard = new Map<string, Comment[]>();
  for (const row of commentRows) {
    const cardId = String(row.card_id);
    commentsByCard.set(cardId, [...(commentsByCard.get(cardId) ?? []), {
      id: String(row.id),
      authorName: profileNames.get(String(row.author_id)) ?? "Pactios",
      body: String(row.body),
      createdAt: String(row.created_at),
    }]);
  }

  return {
    boards: boardRows.map((row) => ({
      id: String(row.id), title: String(row.title), description: String(row.description), theme: row.theme as Accent,
      position: Number(row.position), archivedAt: row.archived_at ? String(row.archived_at) : null,
    })),
    columns: columnRows.map((row) => ({
      id: String(row.id), boardId: String(row.board_id), title: String(row.title), accent: row.accent as Accent,
      position: Number(row.position), archivedAt: row.archived_at ? String(row.archived_at) : null,
    })),
    cards: cardRows.map((row) => ({
      id: String(row.id), boardId: String(row.board_id), columnId: String(row.column_id), title: String(row.title),
      description: String(row.description), accent: row.accent as Accent, category: String(row.category),
      assigneeIds: assigneeRows.filter((item) => String(item.card_id) === String(row.id)).map((item) => String(item.user_id)),
      labels: [], startDate: row.start_date ? String(row.start_date) : null, dueDate: row.due_date ? String(row.due_date) : null,
      position: Number(row.position), checklists: checklistsByCard.get(String(row.id)) ?? [], comments: commentsByCard.get(String(row.id)) ?? [],
      archivedAt: row.archived_at ? String(row.archived_at) : null, updatedAt: String(row.updated_at), revision: Number(row.revision),
    })),
    videos: videoRows.map(videoFromRow),
    profiles,
  };
}

export async function insertBoard(board: Board, workspaceId: string) {
  const { error } = await client().from("boards").insert({
    id: board.id, workspace_id: workspaceId, title: board.title,
    description: board.description, theme: board.theme, position: board.position,
  });
  if (error) throw new Error(error.message);
}

export async function insertCard(card: Card, workspaceId: string) {
  const db = client();
  const { error } = await db.from("cards").insert({
    id: card.id, workspace_id: workspaceId, board_id: card.boardId, column_id: card.columnId, title: card.title,
    description: card.description, accent: card.accent, category: card.category, start_date: card.startDate,
    due_date: card.dueDate, position: card.position, revision: card.revision,
  });
  if (error) throw new Error(error.message);
  if (card.assigneeIds.length) {
    const { error: assigneeError } = await db.from("card_assignees").insert(card.assigneeIds.map((userId) => ({ workspace_id: workspaceId, card_id: card.id, user_id: userId })));
    if (assigneeError) throw new Error(assigneeError.message);
  }
}

export async function persistCard(card: Card) {
  const db = client();
  const { error } = await db.from("cards").update({
    column_id: card.columnId, title: card.title, description: card.description, accent: card.accent, category: card.category,
    start_date: card.startDate, due_date: card.dueDate, position: card.position, revision: card.revision, updated_at: card.updatedAt,
  }).eq("id", card.id);
  if (error) throw new Error(error.message);
  const items = card.checklists.flatMap((checklist) => checklist.items.map((item) => ({ id: item.id, title: item.title, completed: item.completed, position: item.position })));
  if (items.length) {
    const { error: itemError } = await db.from("checklist_items").upsert(items, { onConflict: "id" });
    if (itemError) throw new Error(itemError.message);
  }
}

export async function insertColumn(column: BoardColumn, workspaceId: string) {
  const { error } = await client().from("board_columns").insert({
    id: column.id, workspace_id: workspaceId, board_id: column.boardId, title: column.title,
    accent: column.accent, position: column.position,
  });
  if (error) throw new Error(error.message);
}

export async function persistColumnName(columnId: string, title: string) {
  const { error } = await client().from("board_columns").update({ title, updated_at: new Date().toISOString() }).eq("id", columnId);
  if (error) throw new Error(error.message);
}

export async function archiveColumn(columnId: string) {
  const { error } = await client().from("board_columns").update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", columnId);
  if (error) throw new Error(error.message);
}

export async function archiveBoard(boardId: string) {
  const { error } = await client().from("boards").update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", boardId);
  if (error) throw new Error(error.message);
}

export async function archiveCardRecord(cardId: string) {
  const { error } = await client().from("cards").update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", cardId);
  if (error) throw new Error(error.message);
}

export async function insertVideo(video: VideoRecord, workspaceId: string) {
  const { error } = await client().from("videos").insert({
    id: video.id,
    workspace_id: workspaceId,
    audience: video.audience,
    title: video.title,
    batch: video.batch || null,
    status: video.status,
    pillar: video.pillar || null,
    funnel: video.funnel || null,
    claim_risk: video.claim_risk || null,
    goal: video.goal || null,
    pain: video.pain || null,
    belief_shift: video.belief_shift || null,
    mechanism: video.mechanism || null,
    proof: video.proof || null,
    cta: video.cta || null,
    hook_id: null,
    hook_file: video.hook_file || null,
    hook_target: typeof video.hook_target === "number" ? video.hook_target : null,
    bridge: video.bridge || null,
    script: video.script || null,
    duration: typeof video.duration === "number" ? video.duration : null,
    visuals: video.visuals || null,
    overlay_text: video.overlay_text || null,
    audio: video.audio || null,
    raw_file: video.raw_file || null,
    export_file: video.export_file || null,
    platform: video.platform || null,
    publish_date: video.publish_date,
    caption: video.caption || null,
    cover: video.cover || null,
    post_url: video.post_url || null,
    learning: video.learning || null,
  });
  if (error) throw new Error(error.message);
}

export async function persistVideoStatus(videoId: string, status: string) {
  const { error } = await client().from("videos").update({ status, updated_at: new Date().toISOString() }).eq("id", videoId);
  if (error) throw new Error(error.message);
}

export async function archiveVideoRecord(videoId: string) {
  const now = new Date().toISOString();
  const { error } = await client().from("videos").update({ archived_at: now, updated_at: now }).eq("id", videoId);
  if (error) throw new Error(error.message);
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function text(value: unknown) {
  return value == null ? "" : String(value);
}

// Every optional video column is nullable in Postgres, but the interface treats
// them as strings that pages call .trim() on.
export function videoFromRow(row: Row): VideoRecord {
  return {
    id: String(row.id),
    audience: text(row.audience),
    title: text(row.title),
    batch: text(row.batch),
    status: text(row.status),
    pillar: text(row.pillar),
    funnel: text(row.funnel),
    claim_risk: text(row.claim_risk),
    goal: text(row.goal),
    pain: text(row.pain),
    belief_shift: text(row.belief_shift),
    mechanism: text(row.mechanism),
    proof: text(row.proof),
    cta: text(row.cta),
    hook_id: row.hook_id ? String(row.hook_id) : null,
    hook_file: text(row.hook_file),
    hook_target: typeof row.hook_target === "number" ? row.hook_target : "",
    bridge: text(row.bridge),
    script: text(row.script),
    duration: typeof row.duration === "number" ? row.duration : "",
    visuals: text(row.visuals),
    overlay_text: text(row.overlay_text),
    audio: text(row.audio),
    raw_file: text(row.raw_file),
    export_file: text(row.export_file),
    platform: text(row.platform),
    publish_date: row.publish_date ? String(row.publish_date) : null,
    caption: text(row.caption),
    cover: text(row.cover),
    post_url: text(row.post_url),
    learning: text(row.learning),
    created_at: text(row.created_at),
    updated_at: text(row.updated_at),
  };
}
